import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import HomeIcon from '../../assets/designSystem/icons/footer/Home.svg';
import ConditionsIcon from '../../assets/designSystem/icons/footer/Conditions.svg';
import AssessmentIcon from '../../assets/designSystem/icons/footer/Assessment.svg';
import EmergencyIcon from '../../assets/designSystem/icons/footer/Emergency.svg';
import FirstAidIcon from '../../assets/designSystem/icons/footer/Firstaid.svg';
import HomeIconActive from '../../assets/designSystem/icons/footer/Home-active.svg';
import ConditionsIconActive from '../../assets/designSystem/icons/footer/Conditions-active.svg';
import AssessmentIconActive from '../../assets/designSystem/icons/footer/Assessment-active.svg';
import EmergencyIconActive from '../../assets/designSystem/icons/footer/Emergency-active.svg';
import FirstAidIconActive from '../../assets/designSystem/icons/footer/Firstaid-active.svg';
import './footer.css';
import ColoredBar from '../ColoredBar/ColoredBar';

const VERSION_STORAGE_KEY = '@kitoApp/version';
const CHILD_HOME_ROUTE = '/homepage';
const CHILD_TAB_STORAGE_KEY = 'homePageChildTab';
const VERSION_CHANGE_EVENT = 'kito:version-change';
const CHILD_TAB_CHANGE_EVENT = 'kito:child-tab-change';

const footerItems = [
  {
    labelKey: 'footer.home',
    defaultIcon: HomeIcon,
    activeIcon: HomeIconActive,
    route: '/homepage',
  },
  {
    labelKey: 'footer.conditions',
    defaultIcon: ConditionsIcon,
    activeIcon: ConditionsIconActive,
    route: '/symptoms',
    childTab: 'signs',
  },
  {
    labelKey: 'footer.assessment',
    defaultIcon: AssessmentIcon,
    activeIcon: AssessmentIconActive,
    route: '/assessment',
    childDisabled: true,
  },
  {
    labelKey: 'footer.firstAid',
    defaultIcon: FirstAidIcon,
    activeIcon: FirstAidIconActive,
    route: '/firstaid-adult',
    childTab: 'firstAid',
  },
  {
    labelKey: 'footer.emergency',
    defaultIcon: EmergencyIcon,
    activeIcon: EmergencyIconActive,
    route: '/home-emergency',
  },
];

function readVersion() {
  return window.localStorage.getItem(VERSION_STORAGE_KEY) || 'Adult';
}

function useVersion() {
  const [version, setVersion] = useState(readVersion);

  useEffect(() => {
    const sync = () => setVersion(readVersion());
    window.addEventListener(VERSION_CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(VERSION_CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  return version;
}

export default function Footer() {
  const isChild = useVersion() === 'Child';

  return (
    <div className="footer__container">
      <div className="footer__items__container">
        {footerItems.map((item) => (
          <FooterItem key={item.labelKey} {...item} isChild={isChild} />
        ))}
      </div>
      <ColoredBar big />
    </div>
  );
}

function FooterItem({ defaultIcon, labelKey, activeIcon, route, childTab, childDisabled, isChild }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const label = t(labelKey);

  const disabled = isChild && Boolean(childDisabled);
  const opensChildTab = isChild && Boolean(childTab);
  const selectedRoute = !disabled && !opensChildTab && location.pathname === route;

  const className = `footer__item__container${selectedRoute ? ' footer__item__active' : ''}${
    disabled ? ' footer__item__disabled' : ''
  }`;

  const content = (
    <>
      <img
        src={selectedRoute ? activeIcon : defaultIcon}
        alt={`${label} item`}
        className="footer__icon"
        width={20}
        height={20}
      />
      <p className="footer__label">{label}</p>
    </>
  );

  if (disabled) {
    return (
      <button type="button" className={className} disabled aria-disabled="true">
        {content}
      </button>
    );
  }

  if (opensChildTab) {
    const openChildTab = () => {
      window.localStorage.setItem(CHILD_TAB_STORAGE_KEY, childTab);
      window.dispatchEvent(new CustomEvent(CHILD_TAB_CHANGE_EVENT, { detail: childTab }));
      navigate(CHILD_HOME_ROUTE);
    };

    return (
      <button type="button" className={className} onClick={openChildTab}>
        {content}
      </button>
    );
  }

  return (
    <Link to={route} className={className}>
      {content}
    </Link>
  );
}

FooterItem.propTypes = {
  defaultIcon: PropTypes.string.isRequired,
  labelKey: PropTypes.string.isRequired,
  activeIcon: PropTypes.string.isRequired,
  route: PropTypes.string.isRequired,
  childTab: PropTypes.string,
  childDisabled: PropTypes.bool,
  isChild: PropTypes.bool,
};

FooterItem.defaultProps = {
  childTab: undefined,
  childDisabled: false,
  isChild: false,
};
